import { financialManagementAccountList } from 'apis/fms';
import { getOmsOrderDetailById, getOmsOrderDetailV2 } from 'apis/oms';
import {
  getProductAttributeCodeList,
  getProductUpdateInfoNew,
  printReceiptProductName,
} from 'apis/pms';
import { getMemberById } from 'apis/ums';
import { ORDER_PAYMENT_STATUS_MAP } from 'constant/oms-order-payment-status';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getTimezoneAbbr } from 'utils/getTimezoneByStockPlace';
import { sumBy } from 'lodash-es';
import { OmsOrderDetail, OrderTaxRegime } from 'types/oms';
import { ProductAttributeCodeNames } from 'types/pms';
import connectToPosPrinter, {
  posPrinterSetting,
} from './connect-to-pos-printer';
import {
  getReceiptAccessories,
  TICKET_ACCESSORIES_CONFIRM_EN,
  TICKET_ACCESSORIES_CONFIRM_FR,
  TICKET_ACCESSORIES_CONFIRM_JA,
  TICKET_ACCESSORIES_CONFIRM_ZH_CN,
  TICKET_PAYMENT_NOTICE_EN,
  TICKET_PAYMENT_NOTICE_FR,
  TICKET_PAYMENT_NOTICE_JA,
  TICKET_PAYMENT_NOTICE_ZH_CN,
  TICKET_STORE_TEXT_EN,
  TICKET_STORE_TEXT_FR,
  TICKET_STORE_TEXT_JA,
  TICKET_STORE_TEXT_ZH_CN,
  weekDetail,
} from 'pages/prints/receipt/utils';
import { STOCK_PLACE_MAP } from 'constant/stock-place';
import { CURRENCY_MAP } from 'constant/currency';
import { ORDER_STATUS_MAP } from 'constant/oms-order-status';
import { LANGUAGE_MAP } from 'constant/language';
import { shouldDisplayFranceTaxToCustomer } from 'constants/france-tax';
import { thousands, roundMoney } from 'utils/tools';

type ReceiptLang =
  | typeof LANGUAGE_MAP.JA
  | typeof LANGUAGE_MAP.EN
  | typeof LANGUAGE_MAP.FR
  | typeof LANGUAGE_MAP.ZH_CN;

dayjs.extend(utc);
dayjs.extend(timezone);

const currencySymbolMap: Record<string, string> = {
  [CURRENCY_MAP.JPY]: '¥',
  [CURRENCY_MAP.SGD]: 'SGD',
  [CURRENCY_MAP.HKD]: 'HKD',
  [CURRENCY_MAP.AED]: 'AED',
  [CURRENCY_MAP.EUR]: 'EUR',
  [CURRENCY_MAP.CNY]: 'CNY',
  [CURRENCY_MAP.USD]: 'USD',
};

const escapeXmlText = (value?: string) => {
  if (!value) return '';

  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const getReceiptLang = (actualCurrency: string): ReceiptLang => {
  if (actualCurrency === CURRENCY_MAP.JPY) return LANGUAGE_MAP.JA;
  if (actualCurrency === CURRENCY_MAP.EUR) return LANGUAGE_MAP.FR;
  if (actualCurrency === CURRENCY_MAP.CNY) return LANGUAGE_MAP.ZH_CN;

  return LANGUAGE_MAP.EN;
};

const getAccessoryLabel = (
  item: {
    labelJa?: string;
    labelEn?: string;
    labelCn?: string;
    label?: string;
  },
  lang: ReceiptLang,
) => {
  if (lang === LANGUAGE_MAP.ZH_CN) return item.labelCn || item.label || '';
  if (lang === LANGUAGE_MAP.JA) return item.labelJa || item.label || '';
  return item.labelEn || item.label || '';
};

const getReceiptText = <T>(map: Record<ReceiptLang, T>, lang: ReceiptLang) =>
  map[lang];

const formatReceiptAmount = (amount: number, lang: ReceiptLang) =>
  thousands(amount, lang === LANGUAGE_MAP.FR ? ' ' : ',');

const formatReceiptMoney = (
  symbol: string,
  amount: number,
  lang: ReceiptLang,
  negative = false,
) => {
  return `${negative ? '-' : ''}${symbol} ${formatReceiptAmount(amount, lang)}`;
};

export const getReceiptXmlString = async ({
  omsOrderDetail,
  count = 1,
  addSign = false,
}: {
  omsOrderDetail: OmsOrderDetail & { taxRegime?: OrderTaxRegime | null };
  count?: number;
  addSign?: boolean;
}) => {
  // 防止 count 设置过大，一次性打印太多了
  if (count > 3) {
    count = 3;
  }

  const [{ actualCurrency, productId }] = omsOrderDetail.orderItemList;
  if (!actualCurrency) return '';

  const productInfo = await getProductUpdateInfoNew(productId);
  const { stockPlace } = productInfo;

  const { status, orderType } = omsOrderDetail;
  const receiptLang = getReceiptLang(actualCurrency);
  const currencySymbol = currencySymbolMap[actualCurrency || CURRENCY_MAP.JPY];

  const addressInfo = (() => {
    if (
      stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU ||
      stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU_GST ||
      (!stockPlace && actualCurrency === CURRENCY_MAP.SGD)
    ) {
      return `<text>ASU Company Limited&#10;</text>
<text>#23-05, Ngee Ann City Tower B&#10;</text>
<text>391B Orchard Road&#10;</text>
<text>(+65)6530-3529&#10;</text>`;
    }

    if (stockPlace === STOCK_PLACE_MAP.SINGAPORE_GX) {
      return `<text>GINZA XIAOMA&#10;</text>
<text>#23-05, Ngee Ann City Tower B&#10;</text>
<text>391B Orchard Road&#10;</text>
<text>(+65)6530-3529&#10;</text>`;
    }

    if (stockPlace === STOCK_PLACE_MAP.HONGKONG) {
      return `<text>G/F, Pacific House, 20B&#10;</text>
<text>Queen's Road Central, Hong Kong&#10;</text>
<text>(+852)2662-9721&#10;</text>`;
    }

    if (stockPlace === STOCK_PLACE_MAP.DUBAI) {
      return `<text>Room102, 32 Marasi Drive Street&#10;</text>
<text>The Binary by Omniyat, Business Bay&#10;</text>
<text>Dubai United Arab Emirates&#10;</text>`;
    }

    if (stockPlace === STOCK_PLACE_MAP.UMEDA) {
      return `<text>〒530-0001&#10;</text>
<text>大阪府大阪市北区梅田1丁目8-17&#10;</text>
<text>大阪第一生命ビル1階&#10;</text>
<text>06-6467-8153&#10;</text>
<text>登録番号: T6011101072905&#10;</text>
`;
    }

    if (stockPlace === STOCK_PLACE_MAP.FRANCE) {
      return `<text>9 Rue de la Paix, 75002&#10;</text>
<text>Paris, France&#10;</text>
<text>+33 0140280349&#10;</text>
      `;
    }

    if (stockPlace === STOCK_PLACE_MAP.SHANG_HAI) {
      return `<text>上海市黄浦区淮海中路&#10;</text>
<text>222号607室&#10;</text>
<text>+86 153 0162 1883&#10;</text>`;
    }

    if (stockPlace === STOCK_PLACE_MAP.NEW_YORK) {
      return `<text>Trump Tower, Suite 1901&#10;</text>
<text>725 Fifth Avenue&#10;</text>
<text>New York, NY 10022&#10;</text>
<text>United States&#10;</text>
<text>+1 9177488152&#10;</text>
<text>Mon-Sat 10:00-19:00&#10;</text>`;
    }

    return `<text>東京都中央区銀座1-8-21&#10;</text>
    <text>第21中央ビル1F・2F&#10;</text>
    <text>03-6264-5267&#10;</text>
    <text>登録番号: T6011101072905&#10;</text>`;
  })();

  const companyName = (() => {
    if (stockPlace === STOCK_PLACE_MAP.UMEDA) {
      return 'XIAOMA 梅田店';
    }

    if (
      stockPlace === STOCK_PLACE_MAP.JAPAN ||
      stockPlace === STOCK_PLACE_MAP.SINGAPORE_GX
    ) {
      return 'GINZA XIAOMA';
    }

    if (stockPlace === STOCK_PLACE_MAP.DUBAI) {
      return 'XIAOMA DUBAI';
    }

    if (
      stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU ||
      stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU_GST
    ) {
      return 'ASU COMPANY PTE. LTD';
    }

    if (stockPlace === STOCK_PLACE_MAP.FRANCE) {
      return 'XIAOMA PARIS';
    }

    if (stockPlace === STOCK_PLACE_MAP.SHANG_HAI) {
      return 'XIAOMA 上海';
    }

    if (stockPlace === STOCK_PLACE_MAP.NEW_YORK) {
      return 'XIAOMA NEW YORK';
    }

    return 'ASU Company Limited';
  })();

  // 是否打印的是定金小票
  const isDepositReceipt =
    orderType === 2 && status === ORDER_STATUS_MAP.TO_BE_PAID;

  const footerInfo = (() => {
    const storeText = getReceiptText(
      {
        [LANGUAGE_MAP.JA]: TICKET_STORE_TEXT_JA,
        [LANGUAGE_MAP.EN]: TICKET_STORE_TEXT_EN,
        [LANGUAGE_MAP.FR]: TICKET_STORE_TEXT_FR,
        [LANGUAGE_MAP.ZH_CN]: TICKET_STORE_TEXT_ZH_CN,
      },
      receiptLang,
    );
    const paymentNotice = getReceiptText(
      {
        [LANGUAGE_MAP.JA]: TICKET_PAYMENT_NOTICE_JA,
        [LANGUAGE_MAP.EN]: TICKET_PAYMENT_NOTICE_EN,
        [LANGUAGE_MAP.FR]: TICKET_PAYMENT_NOTICE_FR,
        [LANGUAGE_MAP.ZH_CN]: TICKET_PAYMENT_NOTICE_ZH_CN,
      },
      receiptLang,
    );
    const accessoriesConfirm = getReceiptText(
      {
        [LANGUAGE_MAP.JA]: TICKET_ACCESSORIES_CONFIRM_JA,
        [LANGUAGE_MAP.EN]: TICKET_ACCESSORIES_CONFIRM_EN,
        [LANGUAGE_MAP.FR]: TICKET_ACCESSORIES_CONFIRM_FR,
        [LANGUAGE_MAP.ZH_CN]: TICKET_ACCESSORIES_CONFIRM_ZH_CN,
      },
      receiptLang,
    );

    if (isDepositReceipt) {
      return `<text>${storeText}&#10;</text>
<feed/>
<text>${paymentNotice}&#10;</text>
<feed/>
<text>${accessoriesConfirm}&#10;</text>
<feed/>`;
    }

    return `<text>${storeText}&#10;</text>
<feed/>
<text>${accessoriesConfirm}&#10;</text>
<feed/>`;
  })();

  if (
    actualCurrency !== CURRENCY_MAP.JPY &&
    actualCurrency !== CURRENCY_MAP.HKD &&
    actualCurrency !== CURRENCY_MAP.SGD &&
    actualCurrency !== CURRENCY_MAP.AED &&
    actualCurrency !== CURRENCY_MAP.EUR &&
    actualCurrency !== CURRENCY_MAP.CNY &&
    actualCurrency !== CURRENCY_MAP.USD
  ) {
    return '';
  }

  let xmlString = `<text align="center"/>
<image width="358" height="105" color="color_1" mode="mono">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</image>
<text lang="mul"/>
<text align="center"/>
${receiptLang === LANGUAGE_MAP.JA ? `<text>ASU BRAND株式会社&#10;</text>` : ''}
<feed/>
<text align="left"/>
<text>${companyName}&#10;</text>
${addressInfo}
<text align="center"/>
<text>----------------------------------------------&#10;</text>
<text width="2" height="2"/>
<feed/>
<text>【${getReceiptText(
    {
      [LANGUAGE_MAP.JA]: '領収書',
      [LANGUAGE_MAP.EN]: 'Receipt',
      [LANGUAGE_MAP.FR]: 'Reçu',
      [LANGUAGE_MAP.ZH_CN]: '收据',
    },
    receiptLang,
  )}】&#10;</text>
<text align="left"/>
<text width="1" height="1"/>
<feed/>`;

  const { createTime, orderItemList, paymentTime, deliveryTime } =
    omsOrderDetail;
  if (deliveryTime || paymentTime || createTime) {
    const dayjsObj = dayjs(deliveryTime || paymentTime || createTime);
    const day = dayjsObj.format(
      receiptLang === LANGUAGE_MAP.JA ? 'YYYY年MM月DD日' : 'YYYY-MM-DD',
    );
    const time = dayjsObj.format(
      receiptLang === LANGUAGE_MAP.JA ? 'HH時mm分' : 'HH:mm',
    );
    const weekDay = dayjsObj.day();
    const week = weekDetail[receiptLang][weekDay];
    const tzAbbr = getTimezoneAbbr(dayjs.tz.guess());
    const tzPart = tzAbbr ? ` (${tzAbbr})` : '';
    const createTimeString = `${day} ${week} ${time}${tzPart}`;

    // 下单日期
    xmlString = `${xmlString}
<text>${createTimeString}&#10;</text>
<feed/>`;
  }

  const [productNameMap, productInfoList, productAttributeCodeList] =
    await Promise.all([
      printReceiptProductName(orderItemList.map((i) => i.productId)),
      Promise.all(
        orderItemList.map((item) => getProductUpdateInfoNew(item.productId)),
      ),
      getProductAttributeCodeList(),
    ]);
  const productInfoMap = new Map(
    productInfoList.map((productInfo) => [productInfo.id, productInfo]),
  );
  const accessoryOptions =
    productAttributeCodeList.data
      .find((item) => item.name === ProductAttributeCodeNames.ACCESSORIES)
      ?.itemList.map((item) => ({
        value: item.value,
        label: getAccessoryLabel(item, receiptLang),
        labelJa: item.labelJa,
        labelEn: item.labelEn,
        labelCn: item.labelCn,
      })) || [];

  // 商品列表
  orderItemList.forEach(
    (
      { productId, isTaxFree, productPriceActualCurrency, productQuantity },
      index,
    ) => {
      const productNameArr = productNameMap[productId];
      const productInfo = productInfoMap.get(productId);
      const accessories = getReceiptAccessories(
        productInfo?.accessory,
        receiptLang,
        accessoryOptions,
      );
      const accessoriesText = accessories
        .map((accessory) => `<text>${escapeXmlText(accessory)}&#10;</text>`)
        .join('');

      xmlString = `${xmlString}
<text align="left"/>
${
  isTaxFree && shouldDisplayFranceTaxToCustomer(omsOrderDetail.taxRegime)
    ? `<text>${productId}</text>
<text x="400"/>
<text>${getReceiptText(
        {
          [LANGUAGE_MAP.JA]: '免税',
          [LANGUAGE_MAP.EN]: 'Tax Free',
          [LANGUAGE_MAP.FR]: 'Duty Free',
          [LANGUAGE_MAP.ZH_CN]: '免税',
        },
        receiptLang,
      )}&#10;</text>`
    : `<text>${productId}&#10;</text>`
}
<text>${productNameArr[0].slice(
        0,
        receiptLang === LANGUAGE_MAP.JA ? 15 : 30,
      )} x${productQuantity}</text>
<text x="400"/>
<text>${formatReceiptMoney(
        currencySymbol,
        productPriceActualCurrency * productQuantity,
        receiptLang,
      )}&#10;</text>
${productNameArr[1] ? `<text>${productNameArr[1]}&#10;</text>` : ''}
${productNameArr[2] ? `<text>${productNameArr[2]}&#10;</text>` : ''}
${accessoriesText}
<text>Note: __________________________&#10;</text>
<text align="center"/>
${
  index === orderItemList.length - 1
    ? `<text>----------------------------------------------&#10;</text>`
    : `<feed/>`
}`;
    },
  );

  const subtotal = orderItemList.reduce((acc, item) => {
    return (
      acc + (item.productPriceActualCurrency || 0) * (item.productQuantity || 1)
    );
  }, 0);

  // 商品金额
  xmlString = `${xmlString}
<text align="left"/>
<text>${getReceiptText(
    {
      [LANGUAGE_MAP.JA]: '商品金額',
      [LANGUAGE_MAP.EN]: 'Subtotal',
      [LANGUAGE_MAP.FR]: 'Subtotal',
      [LANGUAGE_MAP.ZH_CN]: '商品金额',
    },
    receiptLang,
  )}</text>
<text x="400"/>
<text>${formatReceiptMoney(currencySymbol, subtotal, receiptLang)}&#10;</text>`;

  // 运费
  if (
    omsOrderDetail.freightAmountActualCurrency &&
    omsOrderDetail.freightAmountActualCurrency > 0
  ) {
    xmlString = `${xmlString}
<text>${getReceiptText(
      {
        [LANGUAGE_MAP.JA]: '送料',
        [LANGUAGE_MAP.EN]: 'Shipping',
        [LANGUAGE_MAP.FR]: 'Frais de port',
        [LANGUAGE_MAP.ZH_CN]: '配送费',
      },
      receiptLang,
    )}</text>
<text x="400"/>
<text>${formatReceiptMoney(
      currencySymbol,
      omsOrderDetail.freightAmountActualCurrency,
      receiptLang,
    )}&#10;</text>`;
  }

  // 优惠金额
  if (omsOrderDetail.promotionAmountActualCurrency) {
    xmlString = `${xmlString}
<text>${getReceiptText(
      {
        [LANGUAGE_MAP.JA]: '割引額',
        [LANGUAGE_MAP.EN]: 'Discount',
        [LANGUAGE_MAP.FR]: 'Réduc',
        [LANGUAGE_MAP.ZH_CN]: '折扣金额',
      },
      receiptLang,
    )}</text>
<text x="400"/>
<text>${formatReceiptMoney(
      currencySymbol,
      omsOrderDetail.promotionAmountActualCurrency,
      receiptLang,
      true,
    )}&#10;</text>`;
  }

  // 使用积分
  if (omsOrderDetail.integrationAmount) {
    xmlString = `${xmlString}
<text>${getReceiptText(
      {
        [LANGUAGE_MAP.JA]: '利用ポイント',
        [LANGUAGE_MAP.EN]: 'Use points',
        [LANGUAGE_MAP.FR]: 'Points utilisés',
        [LANGUAGE_MAP.ZH_CN]: '使用积分',
      },
      receiptLang,
    )}</text>
<text x="400"/>
<text>-P${formatReceiptAmount(
      omsOrderDetail.integrationAmount,
      receiptLang,
    )}&#10;</text>`;
  }

  // 优惠券
  if (omsOrderDetail.couponAmount) {
    xmlString = `${xmlString}
<text>${getReceiptText(
      {
        [LANGUAGE_MAP.JA]: 'クーポン',
        [LANGUAGE_MAP.EN]: 'Coupon',
        [LANGUAGE_MAP.FR]: 'Coupon',
        [LANGUAGE_MAP.ZH_CN]: '优惠券',
      },
      receiptLang,
    )}</text>
<text x="400"/>
<text>-￥${formatReceiptAmount(
      omsOrderDetail.couponAmount,
      receiptLang,
    )}&#10;</text>`;
  }

  // 消费税，放在应付总额之前
  const taxLabel = (() => {
    if (stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU_GST) return '(GST 9%)';
    if (stockPlace === STOCK_PLACE_MAP.DUBAI) return '(Tax 5%)';
    if (stockPlace === STOCK_PLACE_MAP.NEW_YORK) return '(Sales Tax 8.875%)';
    return getReceiptText(
      {
        [LANGUAGE_MAP.JA]: '（内消費税 10%）',
        [LANGUAGE_MAP.EN]: '(Tax included)',
        [LANGUAGE_MAP.FR]: 'TVA',
        [LANGUAGE_MAP.ZH_CN]: '（含消费税）',
      },
      receiptLang,
    );
  })();

  // 银座(JP) / 梅田：消费税为 0 也展示该行；其它库存地仍仅在税额非 0 时展示
  const isJapanStore =
    stockPlace === STOCK_PLACE_MAP.JAPAN ||
    stockPlace === STOCK_PLACE_MAP.UMEDA;
  const shouldShowTaxRow =
    shouldDisplayFranceTaxToCustomer(omsOrderDetail.taxRegime) &&
    (!!omsOrderDetail.totalTaxAmount ||
      (isJapanStore && omsOrderDetail.totalTaxAmount === 0));

  if (shouldShowTaxRow) {
    xmlString = `${xmlString}
<text>${taxLabel}</text>
<text x="400"/>
<text>${formatReceiptMoney(
      currencySymbol,
      omsOrderDetail.totalTaxAmount || 0,
      receiptLang,
    )}&#10;</text>`;
  }

  // 应付总额，金额用实付
  xmlString = `${xmlString}
<text>${getReceiptText(
    {
      [LANGUAGE_MAP.JA]: 'お支払い合計',
      [LANGUAGE_MAP.EN]: 'Total',
      [LANGUAGE_MAP.FR]: 'Montant du paiement',
      [LANGUAGE_MAP.ZH_CN]: '应付总额',
    },
    receiptLang,
  )}</text>
<text x="400"/>
<text>${formatReceiptMoney(
    currencySymbol,
    omsOrderDetail.payAmountActualCurrency || 0,
    receiptLang,
  )}&#10;</text>`;

  // お預かり
  const receivedAmount = (() => {
    if (omsOrderDetail.payList && omsOrderDetail.payList.length) {
      return sumBy(
        omsOrderDetail.payList.filter(
          (i) => i.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID,
        ),
        (i) => i.payAmount,
      );
    }

    return omsOrderDetail.payAmountActualCurrency || 0;
  })();
  xmlString = `${xmlString}
<text>${getReceiptText(
    {
      [LANGUAGE_MAP.JA]: 'お預かり',
      [LANGUAGE_MAP.EN]: 'Paid',
      [LANGUAGE_MAP.FR]: 'Payé',
      [LANGUAGE_MAP.ZH_CN]: '收到金额',
    },
    receiptLang,
  )}</text>
<text x="400"/>
<text>${formatReceiptMoney(
    currencySymbol,
    receivedAmount,
    receiptLang,
  )}&#10;</text>`;

  // 支付记录
  const paymentAccountList = await financialManagementAccountList();
  paymentAccountList.push({
    id: '99999',
    accountCode: 'CRYPTO_CURRENCY_TRIPLE_A',
    accountName: getReceiptText(
      {
        [LANGUAGE_MAP.JA]: '暗号通貨(3%手数料込み)',
        [LANGUAGE_MAP.EN]: 'Crypto Currency (incl. 3% fee)',
        [LANGUAGE_MAP.FR]: 'Crypto-monnaie (frais 3% inclus)',
        [LANGUAGE_MAP.ZH_CN]: '加密货币(含3%手续费)',
      },
      receiptLang,
    ),
    accountDescribe: 'Crypto Currency',
    status: 0,
    storeId: 1,
    deleteStatus: 0,
    sort: 0,
    isCash: 0,
  });
  // 检查支付方式中是否包含加密货币
  const hasCryptoPayment = omsOrderDetail.payList?.some(
    (item) => item.payType === 'CRYPTO_CURRENCY_TRIPLE_A',
  );
  const payList = omsOrderDetail.payList
    ?.filter((i) => i.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID)
    .map((i) => ({
      name:
        paymentAccountList.find((d) => d.accountCode === i.payType)
          ?.accountName || i.payType,
      amount: i.payAmount,
    }));
  payList?.forEach(({ name, amount }) => {
    xmlString = `${xmlString}
<text>（${name}）</text>
<text x="400"/>
<text>${formatReceiptMoney(currencySymbol, amount, receiptLang)}&#10;</text>`;
  });

  // お釣り
  const changeAmount = roundMoney(
    receivedAmount - (omsOrderDetail.payAmountActualCurrency || 0),
  );

  // 如果包含加密货币支付，找零显示为0
  const displayChangeAmount = hasCryptoPayment ? 0 : changeAmount;

  xmlString = `${xmlString}
<text>${
    isDepositReceipt
      ? getReceiptText(
          {
            [LANGUAGE_MAP.JA]: '未払い残額',
            [LANGUAGE_MAP.EN]: 'Unpaid balance',
            [LANGUAGE_MAP.FR]: 'Solde restant dû',
            [LANGUAGE_MAP.ZH_CN]: '未支付余额',
          },
          receiptLang,
        )
      : getReceiptText(
          {
            [LANGUAGE_MAP.JA]: 'お釣り',
            [LANGUAGE_MAP.EN]: 'Change',
            [LANGUAGE_MAP.FR]: 'Monnaie',
            [LANGUAGE_MAP.ZH_CN]: '找零',
          },
          receiptLang,
        )
  }</text>
<text x="400"/>
<text>${formatReceiptMoney(
    currencySymbol,
    isDepositReceipt ? -displayChangeAmount : displayChangeAmount,
    receiptLang,
  )}&#10;</text>`;

  // 获得积分
  if (omsOrderDetail.integration) {
    xmlString = `${xmlString}
<text>${getReceiptText(
      {
        [LANGUAGE_MAP.JA]: '獲得ポイント',
        [LANGUAGE_MAP.EN]: 'Earn points',
        [LANGUAGE_MAP.FR]: 'Points gagnés',
        [LANGUAGE_MAP.ZH_CN]: '获得积分',
      },
      receiptLang,
    )}</text>
<text x="400"/>
<text>P${formatReceiptAmount(
      omsOrderDetail.integration,
      receiptLang,
    )}&#10;</text>`;
  }

  // 客人剩下的积分
  if (omsOrderDetail.memberId) {
    const memberDetail = await getMemberById(omsOrderDetail.memberId);
    if (memberDetail.data) {
      xmlString = `${xmlString}
<text>【${getReceiptText(
        {
          [LANGUAGE_MAP.JA]: '累計ポイント',
          [LANGUAGE_MAP.EN]: 'Cumulative points',
          [LANGUAGE_MAP.FR]: 'Points cumulés',
          [LANGUAGE_MAP.ZH_CN]: '累计积分',
        },
        receiptLang,
      )}】</text>
<text x="400"/>
<text>【P${formatReceiptAmount(
        memberDetail.data.integration || 0,
        receiptLang,
      )}】&#10;</text>
<text>${getReceiptText(
        {
          [LANGUAGE_MAP.JA]: '累計ポイントは印刷時点の会員ポイント残高です',
          [LANGUAGE_MAP.EN]: 'Cumulative points shown as of print time',
          [LANGUAGE_MAP.FR]: `Points cumulés affichés au moment de l'impression`,
          [LANGUAGE_MAP.ZH_CN]: '累计积分显示为打印时的会员积分余额',
        },
        receiptLang,
      )}&#10;</text>`;
    }
  }

  // 担当者
  xmlString = `${xmlString}
<text align="center"/>
<text>----------------------------------------------&#10;</text>
<text align="right"/>
<text>${getReceiptText(
    {
      [LANGUAGE_MAP.JA]: '担当者',
      [LANGUAGE_MAP.EN]: 'Accountable',
      [LANGUAGE_MAP.FR]: 'Responsable',
      [LANGUAGE_MAP.ZH_CN]: '负责人',
    },
    receiptLang,
  )}：${omsOrderDetail.staffName || ''}&#10;</text>
<text align="center"/>
<text>----------------------------------------------&#10;</text>`;

  const getFooter = (addSign: boolean) => {
    return `<text align="left"/>
${footerInfo}
<text align="center"/>
<barcode type="code39" hri="none" font="font_a" width="2" height="64">${
      omsOrderDetail.id
    }</barcode>
<feed/>
<text>${getReceiptText(
      {
        [LANGUAGE_MAP.JA]: '受注No',
        [LANGUAGE_MAP.EN]: 'Order No',
        [LANGUAGE_MAP.FR]: 'Commande N° ',
        [LANGUAGE_MAP.ZH_CN]: '订单号',
      },
      receiptLang,
    )}.${omsOrderDetail.id}${
      isDepositReceipt
        ? getReceiptText(
            {
              [LANGUAGE_MAP.JA]: '（内金注文）',
              [LANGUAGE_MAP.EN]: '（Deposit）',
              [LANGUAGE_MAP.FR]: '（Acompte）',
              [LANGUAGE_MAP.ZH_CN]: '（定金订单）',
            },
            receiptLang,
          )
        : ''
    }&#10;</text>
<feed/>
${
  addSign
    ? `<text align="left"/>
<text>${getReceiptText(
        {
          [LANGUAGE_MAP.JA]: '署名',
          [LANGUAGE_MAP.EN]: 'Sign',
          [LANGUAGE_MAP.FR]: 'Signature',
          [LANGUAGE_MAP.ZH_CN]: '签名',
        },
        receiptLang,
      )}:&#10;</text>
<text align="center"/>
<text>----------------------------------------------&#10;</text>
<feed/>`
    : ''
}
<feed/>
<cut type="feed"/>`;
  };

  let outXmlString = '';
  for (let i = 0; i < count; i++) {
    /**
     * 1. 选择了需要打印 sign
     * 2. 第一张小票
     */
    outXmlString += xmlString + getFooter(addSign && i === 0);
  }

  return outXmlString;
};

const printOrderReceipt = async ({
  orderId,
  count = 1,
  addSign = true,
}: {
  orderId: number;
  count?: number;
  addSign?: boolean;
}) => {
  const loadOrderDetailForReceipt = async () => {
    const [{ data: omsOrderDetail }, orderDetailV2] = await Promise.all([
      getOmsOrderDetailById(orderId),
      getOmsOrderDetailV2(orderId),
    ]);
    // taxRegime 仅 Nest V2 返回；Java 详情无此字段
    return {
      ...omsOrderDetail,
      taxRegime: orderDetailV2.taxRegime,
    };
  };

  if (posPrinterSetting.communicationType.get() === 'socket') {
    const printer = await connectToPosPrinter();

    const omsOrderDetail = await loadOrderDetailForReceipt();
    const xmlString = await getReceiptXmlString({
      omsOrderDetail,
      count,
      addSign,
    });
    if (!xmlString) return;

    printer.setXmlString(xmlString);
    printer.send();
    return;
  }

  if (posPrinterSetting.communicationType.get() === 'http') {
    const omsOrderDetail = await loadOrderDetailForReceipt();
    const xmlString = await getReceiptXmlString({
      omsOrderDetail,
      count,
      addSign,
    });
    if (!xmlString) return;

    const printerXmlUrl = `https://${posPrinterSetting.host.get()}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000`;
    const reqXmlString = `
        <s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
            <s:Body>
                <epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">
                    ${xmlString}
                </epos-print>
                </s:Body>
        </s:Envelope>
`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', printerXmlUrl, true);
      xhr.setRequestHeader('Content-Type', 'text/xml; charset=utf-8');
      xhr.setRequestHeader(
        'If-Modified-Since',
        'Thu, 01 Jan 1970 00:00:00 GMT',
      );
      xhr.setRequestHeader('SOAPAction', '""');
      xhr.onreadystatechange = function () {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            const success =
              xhr?.responseXML
                ?.getElementsByTagName('response')[0]
                ?.getAttribute('success') === 'true';
            if (success) {
              resolve(true);
            } else {
              reject('Print failed.');
            }
          } else {
            reject('Network error occured.');
          }
        }
      };

      xhr.send(reqXmlString);
    });
  }
};

export default printOrderReceipt;
